import { useRecoilState, useRecoilValue } from 'recoil';
import { beaconSelector } from '../recoil/selector';
import useBeacon from '../hooks/useBeacon';
import {
  EVENT_PAGE,
  EVENT_REQUEST,
  INACTIVE_BUTTON_TYPE,
  LANDING_TYPE,
  LINK_TYPE_PROMOTION,
  PAGE_NM,
} from '../types/types';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBaseParam } from '../hooks/useBaseParam';
import { fetchPromotionCheck } from '../../infra/api/fetchPromotionCheck';
import * as Atom from '../recoil';
import { mutatePromotionEntry } from '../../infra/api/mutatePromotionEntry';
import { fetchLoanPromotion } from '../../infra/api/fetchLoanPromotion';
import useNav from '../hooks/useNav';
import { useTermBottomsheet } from '../hooks/useTermBottomsheet';
import { useTermFullsheet } from '../hooks/useTermFullsheet';
import { useTerms } from '../hooks/useTerms';
import { fetchCustTotalProductSummary } from '../../infra/api/fetchCustTotalProductSummary';

const usePromotion = () => {
  const params: any = useParams();
  const { navigate } = useNav();
  const [personal] = useRecoilState(Atom.personalInfo);
  const [productSummary, setProductSummary] = useState<any>({});

  // beacon
  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.PROMOTION,
  });
  const sendBeaconClkBtnEngagePromotion = (btnNm: string) => {
    const start = promo.start;
    const end = promo.end;
    beacon.sendClkBtnEngagePromotion({
      properties: {
        btnNm,
        promotion_type: promo.promotion_type,
        promotion_str_dt: start
          ?.substring(0, 8)
          .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
        promotion_end_dt: end
          ?.substring(0, 8)
          .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
        promotion_seq: params?.promoSeq,
      },
    });
  };

  const sendBeaconClkBtnLoanAppPromotion = (
    btnNm: string,
    linkType: string,
  ) => {
    const start = promo.start;
    const end = promo.end;
    beacon.sendClkBtnLoanApp({
      properties: {
        btnNm,
        linkType: linkType,
        promotion_type: promo.promotion_type,
        promotion_str_dt: start
          ?.substring(0, 8)
          .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
        promotion_end_dt: end
          ?.substring(0, 8)
          .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
        promotion_seq: params?.promoSeq,
      },
    });
  };

  const sendBeaconClkBtnAgreePromotion = (btnNm: string) => {
    const start = promo.start;
    const end = promo.end;
    beacon.sendClkBtnAgreePromotion({
      properties: {
        btnNm,
        promotion_type: promo.promotion_type,
        promotion_str_dt: start
          ?.substring(0, 8)
          .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
        promotion_end_dt: end
          ?.substring(0, 8)
          .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
        promotion_seq: params?.promoSeq,
      },
    });
  };

  // popup
  const [popup, setPopup] = useState({
    open: false,
    message: '참여가 완료되었습니다.',
    hasConfirmButton: false,
    hasCancelButton: false,
  });

  // floating
  const [floating, setIsFloating] = useState({
    isFloating: false,
    setFloating: (v: boolean) => {
      setIsFloating((prev) => ({
        ...prev,
        isFloating: v,
      }));
    },
  });

  // promotion
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [promo, setPromo] = useState<any>({});
  const [isEntry, setIsEntry] = useState<boolean>(false);
  const getPromotion = useBaseParam(fetchLoanPromotion, {
    promotionSeq: Number(params.promoSeq),
  });
  const getPromotionCheck = useBaseParam(fetchPromotionCheck, {
    custSeq: personal.custSeq,
    promotionSeq: Number(params.promoSeq),
  });
  const getCustTotalProductSummary = useBaseParam(
    fetchCustTotalProductSummary,
    {
      custSeq: personal.custSeq,
    },
  );

  useEffect(() => {
    (async () => {
      const [resProductSummary, resPromotion, resPromotionCheck] =
        await Promise.all([
          personal.custSeq && getCustTotalProductSummary(),
          getPromotion(),
          // 신청 여부 v
          personal.custSeq && getPromotionCheck(),
        ]);

      if (resProductSummary?.data?.isSucceed === true) {
        setProductSummary(resProductSummary?.data?.result);
      }

      if (resPromotion?.data?.isSucceed === true) {
        const result = resPromotion.data.result;
        setPromo(() => {
          return { ...result };
        });

        beacon.sendViewPage({
          properties: {
            promotion_type: result?.type,
            promotion_str_dt: result?.promotionStart
              ?.substring(0, 8)
              .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
            promotion_end_dt: result?.promotionEnd
              ?.substring(0, 8)
              .replace(/(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3'),
            promotion_seq: promo?.promotionSeq,
          },
        });
      }

      if (resPromotionCheck?.data?.isSucceed === true) {
        setIsEntry(resPromotionCheck?.data?.result?.isEntry);
      }

      setIsLoading(true);
    })();
  }, []);

  const preRequestToday =
    productSummary?.dayAgo === 0 && productSummary?.preRequestInfoChk === true;

  const isInactiveButton = (
    inactiveButtonYn: 'Y' | 'N',
    inactiveButtonRequirement: INACTIVE_BUTTON_TYPE,
  ) => {
    if (inactiveButtonYn === 'N') return false;
    else {
      if (
        inactiveButtonRequirement === INACTIVE_BUTTON_TYPE.promotion_participate
      ) {
        return isEntry;
      } else if (
        inactiveButtonRequirement === INACTIVE_BUTTON_TYPE.pre_request_today
      ) {
        return preRequestToday;
      }
    }
    return false;
  };

  // term
  const terms = useTerms();
  const mtgTerms = useTerms({
    EVENT_STATE: 'mtg',
    ref_page: PAGE_NM.MTG_HOME,
  });

  const termList = useMemo(
    () =>
      promo?.promotionTerms?.termsList?.map(
        (item: {
          title: string;
          requiredYn: 'Y' | 'N';
          promotionTermsSeq: number | string;
          termsFileUrl: string;
        }) => ({
          text: item.title,
          required: item.requiredYn === 'Y',
          guideText: '',
          termCd: item.promotionTermsSeq || '',
          path: item.termsFileUrl,
        }),
      ) ?? [],
    [promo?.promotionTerms?.termsList],
  );
  const termActiveCondition =
    promo?.promotionTerms?.termsButtonActiveRequirement;

  const termBottomsheet = useTermBottomsheet(termList, termActiveCondition);

  const termFullsheet = useTermFullsheet();
  const openTermFullsheet = (checkItemIdx: number) => {
    const { title, path, termsCd } = termBottomsheet.findItem(checkItemIdx);
    termFullsheet.open(title, path, termsCd);
  };
  const putPromotionParam = {
    custSeq: personal.custSeq,
    promotionSeq: Number(params.promoSeq),
    promotionTermsList: termBottomsheet.checkList.map((item: any) => ({
      promotionTermsSeq: item.termCd,
      agreeYn: item.checked ? 'Y' : 'N',
    })),
  };
  const putPromotionEntry = useBaseParam(
    mutatePromotionEntry,
    putPromotionParam,
  );

  const { navigateByLinkType } = useNav();

  const navigateMain = () => {
    navigate('/main');
  };

  const onClickCompareButton = (e: any) => {
    const btnNm = e.target.innerHTML ?? '';
    beacon.sendClkBtnLoanApp({
      properties: { btnNm },
    });
    goToCreditMain();
  };

  const goToCreditMain = () => {
    if (preRequestToday) {
      navigate('/main');
    } else {
      terms.showTerm();
    }
  };

  const actByPromotionLink = (
    linkType: LANDING_TYPE | LINK_TYPE_PROMOTION,
    redirectionUrl?: string,
    btnName?: string,
  ) => {
    if (linkType === LANDING_TYPE.out_link && redirectionUrl) {
      navigateByLinkType(linkType, redirectionUrl);
    } else if (linkType === LANDING_TYPE.in_app) {
      // 추후 추가
    } else if (linkType === LANDING_TYPE.in_link) {
      navigateByLinkType(linkType, redirectionUrl);
    } else if (linkType === LINK_TYPE_PROMOTION.terms) {
      sendBeaconClkBtnEngagePromotion(btnName ?? '이벤트 참여하기 CTA');
      termBottomsheet.open();
    } else if (linkType === LINK_TYPE_PROMOTION.credit_loan) {
      sendBeaconClkBtnLoanAppPromotion(btnName ?? '신용대출 비교', linkType);
      goToCreditMain();
    } else if (linkType === LINK_TYPE_PROMOTION.mtg_loan) {
      sendBeaconClkBtnLoanAppPromotion(btnName ?? '주택담보대출', linkType);
      mtgTerms.showTerm({ EVENT_STATE: 'mtg' });
    }
  };

  const applyPromotion = async () => {
    const res = await putPromotionEntry();
    if (res?.data?.isSucceed === true && res?.data?.result) {
      setPopup((prev) => ({
        ...prev,
        open: true,
      }));

      // 프로모션 신청 완료 후 1초 뒤 홈으로 이동
      setTimeout(() => {
        setPopup((prev) => ({
          ...prev,
          open: false,
        }));
        navigateMain();
      }, 1000);
    }
  };

  const submitEvent = async () => {
    sendBeaconClkBtnAgreePromotion('이벤트 약관 동의 CTA');
    if (preRequestToday) {
      await applyPromotion();
      return;
    }
    //
    if (params?.media === 'lgu') {
      await terms.processAgreeTermLgu(async () => {
        return await putPromotionEntry();
      });
    } else {
      terms.navigateTerm({
        EVENT_PAGE: EVENT_PAGE.PROMOTION,
        EVENT_REQUEST: EVENT_REQUEST.AGREE,
        EVENT_STATE: '',
        EVENT_OPTION: putPromotionParam,
      });
    }
  };

  const termAgreeButtonName =
    promo?.promotionTerms?.promotionAgreeButtonName || '';

  return {
    isLoading,

    sendBeaconClkBtnEngagePromotion,
    sendBeaconClkBtnAgreePromotion,

    media: params?.media,
    promo,
    isEntry,
    TERM_PROMO: promo?.promotionTerms?.termsList,

    onClickCompareButton,
    actByPromotionLink,
    isInactiveButton,
    applyPromotion,
    promoSeq: params?.promoSeq,
    putPromotionEntry,

    popup,
    floating,

    terms,
    termBottomsheet,
    termAgreeButtonName,
    termFullsheet,
    openTermFullsheet,

    submitEvent,
  };
};

export default usePromotion;
